/**
 * OrderCheckoutPage.js
 * Two-panel checkout page for ordering Recent Magazine copies.
 * Left panel: magazine details with dynamic edition month/year
 * Right panel: payment form with qty selector (₹399 × qty), Razorpay integration
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, AlertCircle, Minus, Plus } from 'lucide-react';
import { vendorService, authService } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import authStorage from '../../utils/authStorage';

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const COPY_FEATURES = [
  'Get your physical copy delivered to your doorstep.',
  'Premium print quality',
  'Exclusive architect & designer features',
  'Industry insights and trends',
  'Project showcases from leading firms',
  'Perfect for offices, studios, and libraries',
];

const OrderCheckoutPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const user = JSON.parse(authStorage.getItem('user') || '{}');
  const [userName, setUserName] = useState(user.name || '');
  const userEmail = user.emailId || user.username || '';
  const [userPhone, setUserPhone] = useState(user.mobileNo || user.phone || '');

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState({ delivery_address: '', pincode: '' });
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      if (userEmail) {
        const res = await authService.getUserProfile(userEmail);
        if (res.success && res.data) {
          if (res.data.name) setUserName(res.data.name);
          if (res.data.mobileNo) {
            setUserPhone(res.data.mobileNo);
            // Cache in authStorage so it's cached next time
            authStorage.setItem('user', JSON.stringify({
              ...user,
              name: res.data.name,
              mobileNo: res.data.mobileNo
            }));
          }
        }
      }
    } catch (err) {
      console.error("Error fetching user profile for copy checkout user info:", err);
    }
  };

  const fetchConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await vendorService.getMagazineConfig();
      if (res.success && res.data) setConfig(res.data);
    } catch { /* silent */ }
    finally { setConfigLoading(false); }
  };

  const copyPrice   = config?.copy_price ?? 399;
  const totalAmount = copyPrice * quantity;
  const edition     = config ? `${config.recent_month} ${config.recent_year}` : 'Loading...';

  const decreaseQty = () => setQuantity((q) => Math.max(1, q - 1));
  const increaseQty = () => setQuantity((q) => q + 1);

  const validate = () => {
    const errs = {};
    if (!form.delivery_address.trim()) errs.delivery_address = 'Delivery address is required.';
    if (!form.pincode.trim()) errs.pincode = 'Pincode is required.';
    else if (!/^\d{6}$/.test(form.pincode.trim())) errs.pincode = 'Pincode must be 6 digits.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePay = async () => {
    if (!validate()) return;
    setPaying(true);

    try {
      const sdkLoaded = await loadRazorpay();
      if (!sdkLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        return;
      }

      // Create order
      const orderRes = await vendorService.createMagazineOrder({
        type: 'COPY',
        quantity,
        delivery_address: form.delivery_address.trim(),
        pincode: form.pincode.trim(),
        name: userName,
        phone: userPhone,
      });

      if (!orderRes.success) {
        toast.error(orderRes.error || 'Failed to create payment order.');
        return;
      }

      const { order_id, amount_paise, key_id } = orderRes.data;

      const razorpayOptions = {
        key: key_id,
        amount: amount_paise,
        currency: 'INR',
        name: 'Top Interiors India',
        description: `TII Magazine (${edition}) — ${quantity} copy${quantity > 1 ? 's' : ''}`,
        order_id,
        prefill: {
          name: userName,
          email: userEmail,
          contact: userPhone,
        },
        // notes are included in the Razorpay webhook payload — backend uses product_type
        // to identify order type reliably, independent of price or description text.
        notes: {
          product_type: 'COPY',
          Name: userName,
          delivery_address: form.delivery_address.trim(),
          pincode: form.pincode.trim(),
          Quantity: String(quantity),
        },
        theme: { color: '#2ab391' },
        handler: async (response) => {
          const verifyRes = await vendorService.verifyMagazinePayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            type: 'COPY',
          });

          if (verifyRes.success) {
            toast.success(`🎉 Order placed for ${quantity} copy${quantity > 1 ? 's' : ''}!`);
            navigate('/my-account/magazine/history');
          } else {
            toast.error(verifyRes.error || 'Payment verification failed. Contact support.');
          }
        },
        modal: {
          ondismiss: () => { setPaying(false); }
        },
      };

      const rzp = new window.Razorpay(razorpayOptions);
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/my-account/magazine/subscribe')}
          className="text-sm text-gray-500 hover:text-[#2ab391] transition-colors mb-3 flex items-center gap-1"
        >
          ← Back to Subscribe
        </button>
        <h1 className="text-2xl font-bold text-gray-800">TII Magazine ({edition})</h1>
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Left Panel: Magazine Details ──────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-2">TII Magazine ({edition})</h2>
          <div className="w-8 h-1 bg-[#2ab391] rounded-full mb-5" />

          <ul className="space-y-3">
            {COPY_FEATURES.map((feature, idx) => (
              <li
                key={idx}
                className={`flex items-start gap-2 text-sm ${idx === 0 ? 'font-semibold text-gray-800' : 'text-gray-700'}`}
              >
                <CheckCircle size={15} className="text-[#2ab391] mt-0.5 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          <p className="text-sm text-gray-500 mt-5 italic">
            For bulk corporate orders, please contact our team.
          </p>
        </div>

        {/* ── Right Panel: Payment Form ─────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-800 mb-2">Payment Details</h2>
          <div className="w-8 h-1 bg-[#2ab391] rounded-full mb-6" />

          <div className="space-y-4">
            {/* Name (auto-fetched, read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userName}
                readOnly
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
              />
            </div>

            {/* Email (auto-fetched, read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={userEmail}
                readOnly
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
              />
            </div>

            {/* Phone (auto-fetched, read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <span className="flex items-center px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm whitespace-nowrap">
                  IN +91
                </span>
                <input
                  type="text"
                  value={userPhone}
                  readOnly
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
                />
              </div>
            </div>

            {/* Delivery Address */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Delivery Address <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.delivery_address}
                onChange={(e) => {
                  setForm((f) => ({ ...f, delivery_address: e.target.value }));
                  if (e.target.value.trim()) {
                    setErrors((prev) => {
                      const updated = { ...prev };
                      delete updated.delivery_address;
                      return updated;
                    });
                  }
                }}
                rows={3}
                placeholder="Enter your full delivery address"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2ab391] focus:border-transparent ${
                  errors.delivery_address ? 'border-red-400' : 'border-gray-200'
                }`}
              />
              {errors.delivery_address && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.delivery_address}
                </p>
              )}
            </div>

            {/* Pincode */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.pincode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 6) {
                    setForm((f) => ({ ...f, pincode: val }));
                    if (val.length === 6) {
                      setErrors((prev) => {
                        const updated = { ...prev };
                        delete updated.pincode;
                        return updated;
                      });
                    }
                  }
                }}
                placeholder="6-digit pincode"
                maxLength={6}
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] focus:border-transparent ${
                  errors.pincode ? 'border-red-400' : 'border-gray-200'
                }`}
              />
              {errors.pincode && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.pincode}
                </p>
              )}
            </div>

            {/* Final Amount with quantity selector */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Final Amount <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3">
                {/* Price display */}
                <div className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm font-semibold">
                  ₹{totalAmount.toLocaleString('en-IN')}.00
                </div>
                {/* Quantity controls */}
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={decreaseQty}
                    disabled={quantity <= 1}
                    className="w-9 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-10 h-10 flex items-center justify-center text-sm font-semibold text-gray-800 border-x border-gray-200">
                    {quantity}
                  </span>
                  <button
                    onClick={increaseQty}
                    className="w-9 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors text-gray-700"
                    aria-label="Increase quantity"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                ₹{copyPrice}/copy × {quantity} {quantity === 1 ? 'copy' : 'copies'} = ₹{totalAmount.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          {/* Payment icons + Pay button */}
          <div className="mt-6 pt-5 border-t border-gray-100">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-xs text-gray-400 font-medium tracking-wider">UPI &bull; VISA &bull; Mastercard &bull; RuPay</span>
              <button
                onClick={handlePay}
                disabled={paying}
                className="flex items-center gap-2 bg-[#2ab391] hover:bg-[#239c7e] disabled:bg-[#2ab391]/60 text-white px-6 py-3 rounded-lg font-bold text-sm transition-colors shadow-md whitespace-nowrap"
              >
                {paying ? <Loader size={16} className="animate-spin" /> : null}
                Pay ₹ {totalAmount.toLocaleString('en-IN')}.00
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCheckoutPage;
